import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, DollarSign, FileText, TrendingUp, CheckCircle, XCircle, Clock, AlertCircle, ArrowLeft, RefreshCw, Shield, Settings, Database, TestTube, Activity, Download, Search, Filter } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showClientDetailsModal, setShowClientDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [testResults, setTestResults] = useState(null);
  const [testingInProgress, setTestingInProgress] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configType, setConfigType] = useState(null);
  const [editablePlans, setEditablePlans] = useState(null);
  const [editableEmail, setEditableEmail] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Bank Credentials & Payment Providers
  const [bankCredentials, setBankCredentials] = useState([]);
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [paymentIntents, setPaymentIntents] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [intentFilter, setIntentFilter] = useState({
    status: 'all',
    method: 'all',
    days: 30
  });

  // Sistema de Atualização
  const [systemVersion, setSystemVersion] = useState(null);
  const [updateFile, setUpdateFile] = useState(null);
  const [uploadedPackage, setUploadedPackage] = useState(null);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [applyingUpdate, setApplyingUpdate] = useState(false);
  const [updateProgress, setUpdateProgress] = useState([]);
  const [rollbackBackups, setRollbackBackups] = useState([]);
  const [performingRollback, setPerformingRollback] = useState(false);
  const [rollbackProgress, setRollbackProgress] = useState([]);

  // Proteção de Dados
  const [backups, setBackups] = useState([]);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [integrityReport, setIntegrityReport] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      // Carregar estatísticas
      const statsResponse = await axios.get(`${API_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsResponse.data);

      // Carregar clientes
      const clientsResponse = await axios.get(`${API_URL}/admin/clients?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(clientsResponse.data);

      // Carregar transações
      const transactionsResponse = await axios.get(`${API_URL}/admin/transactions?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(transactionsResponse.data);

      // Carregar logs
      const logsResponse = await axios.get(`${API_URL}/admin/logs?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(logsResponse.data.logs || []);

    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores.');
        navigate('/dashboard');
      } else {
        toast.error('Erro ao carregar dados administrativos');
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== SISTEMA DE ATUALIZAÇÃO ====================
  
  const loadSystemVersion = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/system/version`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemVersion(response.data);
    } catch (error) {
      console.error('Erro ao carregar versão:', error);
    }
  };

  const loadUpdateHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/system/update-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUpdateHistory(response.data.history);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        toast.error('❌ Arquivo deve ser um ZIP');
        return;
      }
      setUpdateFile(file);
      toast.info(`📦 Arquivo selecionado: ${file.name}`);
    }
  };

  const handleUploadPackage = async () => {
    if (!updateFile) {
      toast.error('❌ Selecione um arquivo primeiro');
      return;
    }

    const formData = new FormData();
    formData.append('file', updateFile);

    try {
      toast.info('📤 Fazendo upload e validando pacote...');
      
      const response = await axios.post(
        `${API_URL}/admin/system/upload-update`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('✅ Pacote validado com sucesso!');
        setUploadedPackage(response.data);
        setUpdateFile(null);
      } else {
        toast.error('❌ Pacote inválido: ' + response.data.errors.join(', '));
      }
    } catch (error) {
      toast.error('❌ Erro no upload: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleApplyUpdate = async () => {
    if (!uploadedPackage) {
      toast.error('❌ Nenhum pacote válido para aplicar');
      return;
    }

    if (!window.confirm('⚠️ ATENÇÃO: Esta operação irá atualizar TODO O SISTEMA.\n\nUm backup será criado automaticamente.\n\nDeseja continuar?')) {
      return;
    }

    setApplyingUpdate(true);
    setUpdateProgress(['🔄 Iniciando atualização...']);

    try {
      const response = await axios.post(
        `${API_URL}/admin/system/apply-update`,
        { file_path: uploadedPackage.file_path },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setUpdateProgress(response.data.steps);
        toast.success(`✅ Sistema atualizado para versão ${response.data.version}!`);
        
        // Recarregar informações
        await loadSystemVersion();
        await loadUpdateHistory();
        
        setUploadedPackage(null);
        
        // Alertar sobre reinicialização
        if (response.data.steps.some(step => step.includes('REINICIALIZAÇÃO'))) {
          toast.warning('⚠️ Sistema requer reinicialização. Contate o administrador do servidor.', {
            duration: 10000
          });
        }
      } else {
        toast.error('❌ Erro ao aplicar atualização');
        setUpdateProgress(['❌ Erro: ' + response.data.errors.join(', ')]);
      }
    } catch (error) {
      toast.error('❌ Erro crítico: ' + (error.response?.data?.detail || error.message));
      setUpdateProgress(['❌ Erro crítico ao aplicar atualização']);
    } finally {
      setApplyingUpdate(false);
    }
  };

  const handleCancelUpload = () => {
    setUpdateFile(null);
    setUploadedPackage(null);
    setUpdateProgress([]);
  };

  const loadRollbackBackups = async () => {
    try {
      // Buscar backups de pré-atualização
      const response = await axios.get(`${API_URL}/admin/system/data-protection/backups?type=pre_update&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRollbackBackups(response.data.backups || []);
      }
    } catch (error) {
      console.error('Erro ao carregar backups de rollback:', error);
    }
  };

  const handleRollback = async (backupName) => {
    if (!window.confirm(
      `⚠️ ATENÇÃO: REVERTER ATUALIZAÇÃO\n\n` +
      `Isso irá restaurar o sistema para a versão anterior usando o backup:\n${backupName}\n\n` +
      `O sistema precisará ser reiniciado após a reversão.\n\n` +
      `Deseja continuar?`
    )) {
      return;
    }

    setPerformingRollback(true);
    setRollbackProgress(['🔄 Iniciando reversão de atualização...']);

    try {
      toast.info('🔄 Revertendo para versão anterior...');
      
      const response = await axios.post(
        `${API_URL}/admin/system/rollback/${backupName}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setRollbackProgress(response.data.steps);
        toast.success('✅ Sistema revertido com sucesso!');
        
        // Recarregar informações
        await loadSystemVersion();
        await loadUpdateHistory();
        await loadRollbackBackups();
        
        // Alertar sobre reinicialização
        toast.warning(
          '⚠️ Sistema revertido! É necessário reiniciar os serviços.\n\nExecute no servidor:\nsudo supervisorctl restart all',
          { duration: 15000 }
        );
      } else {
        toast.error('❌ Erro ao reverter: ' + response.data.error);
        setRollbackProgress(['❌ Erro: ' + response.data.error]);
      }
    } catch (error) {
      toast.error('❌ Erro crítico ao reverter: ' + (error.response?.data?.detail || error.message));
      setRollbackProgress(['❌ Erro crítico na reversão']);
    } finally {
      setPerformingRollback(false);
    }
  };

  // ==================== PROTEÇÃO DE DADOS ====================
  
  const loadBackups = async () => {
    try {
      console.log('📥 Carregando backups disponíveis...');
      const response = await axios.get(`${API_URL}/admin/system/data-protection/backups?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        console.log('✅ Backups carregados:', response.data.backups);
        setBackups(response.data.backups);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar backups:', error);
      toast.error('Erro ao carregar backups');
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm('Criar backup completo agora?\n\n⚠️ Esta operação pode demorar alguns minutos dependendo do tamanho dos dados.')) {
      return;
    }

    setCreatingBackup(true);
    toast.info('🔒 Criando backup completo...');

    try {
      const response = await axios.post(
        `${API_URL}/admin/system/data-protection/backup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const backup = response.data.backup;
        toast.success(`✅ Backup criado com sucesso!\n${backup.name}\nTamanho: ${backup.size_mb} MB`);
        
        // Recarregar lista
        await loadBackups();
      } else {
        toast.error(`❌ Erro ao criar backup: ${response.data.message}`);
      }
    } catch (error) {
      console.error('❌ Erro ao criar backup:', error);
      toast.error('Erro ao criar backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestoreBackup = async (backupName) => {
    if (!window.confirm(
      `⚠️⚠️⚠️ ATENÇÃO: OPERAÇÃO CRÍTICA ⚠️⚠️⚠️\n\n` +
      `Você está prestes a RESTAURAR o sistema para o backup:\n${backupName}\n\n` +
      `Isso irá:\n` +
      `• Restaurar banco de dados\n` +
      `• Restaurar arquivos\n` +
      `• Restaurar configurações\n\n` +
      `O sistema atual será sobrescrito!\n\n` +
      `Deseja REALMENTE continuar?`
    )) {
      return;
    }

    setRestoringBackup(true);
    toast.info('🔄 Restaurando backup...');

    try {
      const response = await axios.post(
        `${API_URL}/admin/system/data-protection/restore/${backupName}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('✅ Backup restaurado com sucesso!\n\n⚠️ Reinicie os serviços para aplicar mudanças.');
        
        // Mostrar log
        console.log('📋 Log de restauração:', response.data.log);
        
        // Recarregar dados
        await loadBackups();
        await loadSystemVersion();
      } else {
        toast.error(`❌ Erro ao restaurar: ${response.data.error}`);
        console.error('Log:', response.data.log);
      }
    } catch (error) {
      console.error('❌ Erro ao restaurar backup:', error);
      toast.error('Erro ao restaurar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRestoringBackup(false);
    }
  };

  const handleVerifyIntegrity = async () => {
    toast.info('🔍 Verificando integridade dos dados...');

    try {
      const response = await axios.get(
        `${API_URL}/admin/system/data-protection/verify`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setIntegrityReport(response.data.report);
        
        if (response.data.report.status === 'healthy') {
          toast.success('✅ Integridade verificada! Sistema saudável.');
        } else {
          toast.warning('⚠️ Problemas de integridade encontrados. Veja o relatório.');
        }
      }
    } catch (error) {
      console.error('❌ Erro ao verificar integridade:', error);
      toast.error('Erro ao verificar integridade');
    }
  };

  useEffect(() => {
    if (activeTab === 'data-protection') {
      loadBackups();
      handleVerifyIntegrity();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'system-update') {
      loadSystemVersion();
      loadUpdateHistory();
      loadRollbackBackups();
    }
  }, [activeTab]);


  const handleApprovePayment = async (transactionId) => {
    try {
      const response = await axios.post(
        `${API_URL}/admin/approve-payment`,
        {
          transaction_id: transactionId,
          notes: 'Aprovação manual pelo ADMMASTER'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('✅ Pagamento aprovado e plano liberado!');
        loadAdminData();
        setShowApprovalModal(false);
      }
    } catch (error) {
      toast.error('Erro: ' + (error.response?.data?.detail || 'Erro desconhecido'));
    }
  };

  const handleUpdatePlan = async (userId, newPlan) => {
    try {
      const response = await axios.post(
        `${API_URL}/admin/manual-plan-update`,
        {
          user_id: userId,
          new_plan: newPlan,
          reason: 'Liberação manual pelo ADMMASTER'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(`✅ Plano atualizado para ${newPlan}!`);
        loadAdminData();
        setShowPlanModal(false);
      }
    } catch (error) {
      toast.error('Erro: ' + (error.response?.data?.detail || 'Erro desconhecido'));
    }
  };

  const handleBlockUser = async (userId, action) => {
    const newStatus = action === 'block' ? 'blocked' : 'active';
    const confirmMsg = action === 'block' 
      ? '⚠️ Tem certeza que deseja BLOQUEAR este usuário?'
      : '✅ Tem certeza que deseja REATIVAR este usuário?';
    
    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await axios.post(
        `${API_URL}/admin/update-user-status`,
        {
          user_id: userId,
          new_status: newStatus,
          reason: `${action === 'block' ? 'Bloqueio' : 'Reativação'} manual pelo ADMMASTER`
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(`✅ Usuário ${action === 'block' ? 'bloqueado' : 'reativado'} com sucesso!`);
        loadAdminData();
      }
    } catch (error) {
      toast.error('Erro: ' + (error.response?.data?.detail || 'Erro desconhecido'));
    }
  };

  const exportLogs = () => {
    try {
      const dataStr = JSON.stringify(logs, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `admin_logs_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      toast.success('✅ Logs exportados com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar logs');
    }
  };

  // Funções de teste de pagamentos
  const testMercadoPago = async () => {
    setTestingInProgress(true);
    setTestResults(null);
    toast.info('🔌 Testando conexão com Mercado Pago...');

    try {
      // Criar uma preferência de teste
      const response = await axios.post(
        `${API_URL}/payments/create-preference`,
        {
          work_id: 'test-work-id',
          plan: 'professional'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data) {
        setTestResults({
          type: 'mercadopago',
          success: true,
          message: 'Conexão com Mercado Pago funcionando!',
          data: {
            init_point: response.data.init_point,
            preference_id: response.data.id
          }
        });
        toast.success('✅ Mercado Pago está funcionando corretamente!');
      }
    } catch (error) {
      console.error('Erro no teste do Mercado Pago:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      setTestResults({
        type: 'mercadopago',
        success: false,
        message: 'Erro ao conectar com Mercado Pago',
        error: errorMsg
      });
      toast.error('❌ Erro: ' + errorMsg);
    } finally {
      setTestingInProgress(false);
    }
  };

  const testBoleto = async () => {
    setTestingInProgress(true);
    setTestResults(null);
    toast.info('📄 Testando geração de boleto...');

    try {
      // Dados de teste para o boleto (usando novo endpoint que cria transação)
      const testData = {
        plan: 'professional',  // Plano para teste
        work_id: 'BOLETO_TESTE'  // ID temporário para teste
      };
      
      const response = await axios.post(
        `${API_URL}/payment/boleto/generate`,
        testData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      if (response.data) {
        // Verificar se é PDF válido
        const isPDF = response.data.type === 'application/pdf';
        const size = response.data.size;
        
        // Extrair informações da transação dos headers
        const transactionId = response.headers['x-transaction-id'];
        const transactionCode = response.headers['x-transaction-code'];

        setTestResults({
          type: 'boleto',
          success: isPDF && size > 1000,
          message: isPDF ? 'Boleto gerado e transação criada!' : 'Resposta inválida',
          data: {
            fileSize: `${(size / 1024).toFixed(2)} KB`,
            contentType: response.data.type,
            plano: testData.plan,
            transactionId: transactionId || 'N/A',
            transactionCode: transactionCode || 'N/A'
          }
        });

        if (isPDF && size > 1000) {
          // Fazer download do boleto de teste
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = `boleto_${transactionCode || Date.now()}.pdf`;
          link.click();
          toast.success(`✅ Boleto gerado! Transação: ${transactionCode || 'criada'}`);
          
          // Recarregar lista de transações
          loadAdminData();
        } else {
          toast.warning('⚠️ Boleto gerado mas pode estar incompleto');
        }
      }
    } catch (error) {
      console.error('Erro no teste de boleto:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      setTestResults({
        type: 'boleto',
        success: false,
        message: 'Erro ao gerar boleto',
        error: errorMsg
      });
      toast.error('❌ Erro: ' + errorMsg);
    } finally {
      setTestingInProgress(false);
    }
  };

  const testPIX = async () => {
    setTestingInProgress(true);
    setTestResults(null);
    toast.info('💳 Testando geração de QR Code PIX...');

    try {
      // Criar um PIX de teste usando novo endpoint com transação
      const response = await axios.post(
        `${API_URL}/payments/create-pix-transaction`,
        {
          work_id: 'PIX_TESTE',
          plan: 'professional'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data && response.data.success) {
        setTestResults({
          type: 'pix',
          success: true,
          message: 'QR Code PIX gerado e transação criada!',
          data: {
            pix_code: response.data.pix_code ? 'Disponível' : 'Não disponível',
            amount: `R$ ${response.data.amount?.toFixed(2) || '0.00'}`,
            transaction_id: response.data.transaction_id || 'N/A',
            transaction_code: response.data.transaction_code || 'N/A'
          }
        });
        toast.success(`✅ PIX gerado! Transação: ${response.data.transaction_code || 'criada'}`);
        
        // Recarregar lista de transações
        loadAdminData();
      }
    } catch (error) {
      console.error('Erro no teste de PIX:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      setTestResults({
        type: 'pix',
        success: false,
        message: 'Erro ao gerar QR Code PIX',
        error: errorMsg
      });
      toast.error('❌ Erro: ' + errorMsg);
    } finally {
      setTestingInProgress(false);
    }
  };

  // Funções de configuração
  const openConfig = async (type) => {
    setConfigType(type);
    
    // Carregar dados editáveis se for planos ou email
    if (type === 'plans') {
      try {
        console.log('📥 Carregando configuração de planos...');
        const response = await axios.get(`${API_URL}/admin/plans/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('✅ Planos carregados do servidor:', response.data.config);
        console.log('   Individual duration_months:', response.data.config.individual?.duration_months);
        console.log('   Professional duration_months:', response.data.config.professional?.duration_months);
        console.log('   Premium duration_months:', response.data.config.premium?.duration_months);
        
        setEditablePlans(response.data.config);
        
        // Esperar um pouco para garantir que o state foi atualizado
        setTimeout(() => {
          setShowConfigModal(true);
        }, 100);
        
      } catch (error) {
        console.error('❌ Erro ao carregar configuração de planos:', error);
        toast.error('Erro ao carregar configuração de planos');
        setShowConfigModal(true);
      }
    } else if (type === 'email') {
      try {
        const response = await axios.get(`${API_URL}/admin/email/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEditableEmail(response.data.config);
        setShowConfigModal(true);
      } catch (error) {
        console.error('Erro ao carregar configuração de email:', error);
        toast.error('Erro ao carregar configuração de email');
        setShowConfigModal(true);
      }
    } else if (type === 'bank-credentials') {
      // Carregar credenciais bancárias
      setLoadingCredentials(true);
      try {
        const response = await axios.get(`${API_URL}/admin/bank-credentials`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBankCredentials(response.data);
        setShowConfigModal(true);
      } catch (error) {
        console.error('Erro ao carregar credenciais:', error);
        toast.error('Erro ao carregar credenciais bancárias');
        setShowConfigModal(true);
      } finally {
        setLoadingCredentials(false);
      }
    } else if (type === 'payment-providers') {
      // Carregar provedores de pagamento
      setLoadingCredentials(true);
      try {
        const response = await axios.get(`${API_URL}/admin/payment-providers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPaymentProviders(response.data);
        setShowConfigModal(true);
      } catch (error) {
        console.error('Erro ao carregar provedores:', error);
        toast.error('Erro ao carregar provedores de pagamento');
        setShowConfigModal(true);
      } finally {
        setLoadingCredentials(false);
      }
    } else if (type === 'webhooks-logs') {
      // Carregar logs de webhooks
      setLoadingCredentials(true);
      try {
        const response = await axios.get(`${API_URL}/admin/webhooks/logs?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWebhookLogs(response.data.logs || []);
        setShowConfigModal(true);
      } catch (error) {
        console.error('Erro ao carregar logs:', error);
        toast.error('Erro ao carregar logs de webhooks');
        setShowConfigModal(true);
      } finally {
        setLoadingCredentials(false);
      }
    } else if (type === 'payment-intents') {
      // Carregar cobranças/pagamentos
      setLoadingCredentials(true);
      try {
        const [intentsResponse, statsResponse] = await Promise.all([
          axios.get(`${API_URL}/admin/payment-intents?days=${intentFilter.days}&limit=100`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/admin/payment-intents/stats?days=${intentFilter.days}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setPaymentIntents(intentsResponse.data);
        setPaymentStats(statsResponse.data);
        setShowConfigModal(true);
      } catch (error) {
        console.error('Erro ao carregar cobranças:', error);
        toast.error('Erro ao carregar cobranças');
        setShowConfigModal(true);
      } finally {
        setLoadingCredentials(false);
      }
    } else if (type === 'security' || type === 'seo') {
      // Para configurações que ainda não têm backend, abrir modal informativo
      setShowConfigModal(true);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    
    try {
      if (configType === 'plans' && editablePlans) {
        console.log('📤 Enviando configuração de planos:', editablePlans);
        
        const response = await axios.post(
          `${API_URL}/admin/plans/config`,
          editablePlans,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('✅ Resposta do servidor:', response.data);
        
        // Notificar todas as páginas abertas sobre a atualização
        if (response.data.success) {
          try {
            // Importar e usar o serviço de sincronização
            const { default: plansSyncService } = await import('../services/plansSyncService');
            plansSyncService.notifyPlansUpdated(response.data.config);
            console.log('✅ Sincronização notificada');
          } catch (syncError) {
            console.warn('⚠️ Erro ao sincronizar, mas salvo com sucesso:', syncError);
          }
        }
        
        toast.success('✅ Configuração salva! Recarregando página...');
        
        // Fechar modal
        setShowConfigModal(false);
        
        // Forçar reload completo da página para garantir que mudanças apareçam
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        
      } else if (configType === 'email' && editableEmail) {
        await axios.post(
          `${API_URL}/admin/email/config`,
          editableEmail,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('✅ Configuração de e-mail salva com sucesso!');
        setShowConfigModal(false);
      }
      
    } catch (error) {
      console.error('❌ Erro completo ao salvar configuração:', error);
      console.error('❌ Detalhes do erro:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      toast.error(`❌ Erro ao salvar: ${errorMsg}`);
    } finally {
      setSavingConfig(false);
    }
  };

  const getPlanBadge = (plan) => {
    const colors = {
      individual: 'bg-gray-100 text-gray-800 border border-gray-300',
      professional: 'bg-blue-100 text-blue-800 border border-blue-300',
      premium: 'bg-purple-100 text-purple-800 border border-purple-300',
      annual: 'bg-green-100 text-green-800 border border-green-300',
      master: 'bg-red-100 text-red-800 border border-red-300 font-bold'
    };
    return colors[plan] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, text: 'Pendente' },
      approved: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, text: 'Aprovado' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, text: 'Rejeitado' },
      active: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, text: 'Ativo' },
      blocked: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, text: 'Bloqueado' },
      suspended: { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertCircle, text: 'Suspenso' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  // Filtrar clientes
  const filteredClients = clients.filter(client => {
    const matchSearch = 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatus = filterStatus === 'all' || client.status === filterStatus;
    const matchPlan = filterPlan === 'all' || client.plan === filterPlan;

    return matchSearch && matchStatus && matchPlan;
  });

  // Filtrar transações
  const filteredTransactions = transactions.filter(txn => {
    if (filterStatus === 'all') return true;
    return txn.status === filterStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 animate-pulse mx-auto mb-4" />
          <p className="text-white text-lg">Carregando Painel ADMMASTER...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Header Exclusivo ADMMASTER */}
      <div className="bg-gradient-to-r from-red-900 to-red-700 shadow-2xl border-b-4 border-red-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Shield className="w-12 h-12 text-white" />
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                  🔐 PAINEL ADMMASTER
                  <span className="text-xs bg-red-500 px-2 py-1 rounded">SUPERADMIN</span>
                </h1>
                <p className="text-red-100 mt-1">Controle Total do Sistema</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadAdminData}
                className="flex items-center gap-2 px-4 py-2 bg-white text-red-900 rounded-lg hover:bg-red-50 transition font-semibold"
              >
                <RefreshCw className="w-4 h-4" />
                Atualizar
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Sair do Admin
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="border-b border-gray-700">
          <nav className="-mb-px flex space-x-4 overflow-x-auto">
            {[
              { id: 'dashboard', icon: Activity, label: 'Dashboard' },
              { id: 'clients', icon: Users, label: `Clientes (${clients.length})` },
              { id: 'transactions', icon: DollarSign, label: `Transações (${transactions.length})` },
              { id: 'logs', icon: FileText, label: `Logs (${logs.length})` },
              { id: 'tools', icon: TestTube, label: 'Ferramentas' },
              { id: 'system-update', icon: RefreshCw, label: '🔄 Atualização do Sistema' },
              { id: 'data-protection', icon: Shield, label: '🛡️ Proteção de Dados' }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-4 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 transition ${
                    activeTab === tab.id
                      ? 'border-red-500 text-red-500'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Carregando painel ADMMASTER...</p>
            </div>
          </div>
        )}
        
        {/* DASHBOARD TAB */}
        {!loading && activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <button 
                onClick={() => setActiveTab('clients')}
                className="bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg shadow-xl p-6 border border-blue-500 hover:from-blue-800 hover:to-blue-600 transition-all cursor-pointer text-left w-full"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-200">Total de Usuários</p>
                    <p className="text-4xl font-bold text-white">{stats.users.total}</p>
                  </div>
                  <Users className="w-12 h-12 text-blue-300" />
                </div>
                <div className="mt-4 pt-4 border-t border-blue-500">
                  <p className="text-sm text-blue-200">
                    ✅ {stats.users.active} ativos • ⛔ {stats.users.blocked} bloqueados
                  </p>
                </div>
                <p className="text-xs text-blue-300 mt-2">👆 Clique para ver lista</p>
              </button>

              <button
                onClick={() => navigate('/gallery')}
                className="bg-gradient-to-br from-purple-900 to-purple-700 rounded-lg shadow-xl p-6 border border-purple-500 hover:from-purple-800 hover:to-purple-600 transition-all cursor-pointer text-left w-full"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-200">Total de Obras</p>
                    <p className="text-4xl font-bold text-white">{stats.works.total}</p>
                  </div>
                  <FileText className="w-12 h-12 text-purple-300" />
                </div>
                <div className="mt-4 pt-4 border-t border-purple-500">
                  <p className="text-sm text-purple-200">
                    🌍 {stats.works.public} públicas
                  </p>
                </div>
                <p className="text-xs text-purple-300 mt-2">👆 Clique para ver galeria</p>
              </button>

              <div className="bg-gradient-to-br from-green-900 to-green-700 rounded-lg shadow-xl p-6 border border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-200">Certificados</p>
                    <p className="text-4xl font-bold text-white">{stats.certificates.total}</p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-green-300" />
                </div>
                <div className="mt-4 pt-4 border-t border-green-500">
                  <p className="text-sm text-green-200">
                    📜 Certificados gerados
                  </p>
                </div>
              </div>

              <button
                onClick={() => setActiveTab('transactions')}
                className="bg-gradient-to-br from-yellow-900 to-yellow-700 rounded-lg shadow-xl p-6 border border-yellow-500 hover:from-yellow-800 hover:to-yellow-600 transition-all cursor-pointer text-left w-full"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-200">Transações</p>
                    <p className="text-4xl font-bold text-white">{stats.transactions.total}</p>
                  </div>
                  <DollarSign className="w-12 h-12 text-yellow-300" />
                </div>
                <div className="mt-4 pt-4 border-t border-yellow-500">
                  <p className="text-sm text-yellow-200">
                    ⏳ {stats.transactions.pending} pendentes • ✅ {stats.transactions.approved} aprovadas
                  </p>
                </div>
                <p className="text-xs text-yellow-300 mt-2">👆 Clique para ver transações</p>
              </button>
            </div>

            {/* Alert Transações Pendentes */}
            {stats.transactions.pending > 0 && (
              <div className="bg-red-900 border-l-4 border-red-500 p-6 rounded-lg shadow-xl">
                <div className="flex items-center">
                  <AlertCircle className="w-6 h-6 text-red-300 mr-4" />
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      ⚠️ ATENÇÃO: {stats.transactions.pending} transações pendentes!
                    </h3>
                    <p className="text-red-200 mt-1">
                      Verifique a aba "Transações" e aprove manualmente se necessário
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Distribuição por Plano */}
            <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6" />
                Distribuição por Plano
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(stats.users.by_plan).map(([plan, count]) => (
                  <div key={plan} className="text-center bg-gray-700 rounded-lg p-4">
                    <p className="text-3xl font-bold text-white">{count}</p>
                    <p className={`text-sm mt-2 px-3 py-1 rounded-full inline-block ${getPlanBadge(plan)}`}>
                      {plan}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Dashboard Error/Empty State */}
        {!loading && activeTab === 'dashboard' && !stats && (
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-8 text-center">
            <h3 className="text-xl font-bold text-red-400 mb-2">Erro ao carregar dados</h3>
            <p className="text-gray-400 mb-4">Não foi possível carregar as estatísticas do sistema</p>
            <button
              onClick={loadAdminData}
              className="bg-red-600 px-6 py-2 rounded-lg hover:bg-red-700 transition"
            >
              Tentar Novamente
            </button>
          </div>
        )}

        {/* CLIENTES TAB */}
        {!loading && activeTab === 'clients' && (
          <div className="space-y-6">
            {/* Filtros e Pesquisa */}
            <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Buscar por nome, email ou ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-red-500"
                  >
                    <option value="all">Todos os Status</option>
                    <option value="active">Ativos</option>
                    <option value="blocked">Bloqueados</option>
                    <option value="suspended">Suspensos</option>
                  </select>
                </div>
                <div>
                  <select
                    value={filterPlan}
                    onChange={(e) => setFilterPlan(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-red-500"
                  >
                    <option value="all">Todos os Planos</option>
                    <option value="individual">Individual</option>
                    <option value="professional">Professional</option>
                    <option value="premium">Premium</option>
                    <option value="annual">Annual</option>
                    <option value="master">Master</option>
                  </select>
                </div>
              </div>
              <p className="text-gray-400 text-sm mt-4">
                Mostrando {filteredClients.length} de {clients.length} clientes
              </p>
            </div>

            {/* Tabela de Clientes */}
            <div className="bg-gray-800 rounded-lg shadow-xl overflow-hidden border border-gray-700">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Cliente</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Plano</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Obras</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">💰 Data Pagamento</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">📅 Expira em</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">⏳ Contagem</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredClients.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-750 transition">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-white">{client.name}</div>
                            <div className="text-sm text-gray-400">{client.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs font-mono text-gray-400">{client.id.substring(0, 8)}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 text-xs rounded-full ${getPlanBadge(client.plan)}`}>
                            {client.plan}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300">
                          {client.works_count}
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(client.status)}
                        </td>
                        <td className="px-6 py-4 text-sm space-x-2">
                          {client.email !== 'sacminhascomposicoes@gmail.com' ? (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedClient(client);
                                  setShowClientDetailsModal(true);
                                }}
                                className="text-blue-400 hover:text-blue-300 font-medium"
                              >
                                👁️ Ver
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedClient(client);
                                  setShowPlanModal(true);
                                }}
                                className="text-green-400 hover:text-green-300 font-medium"
                              >
                                🔓 Liberar
                              </button>
                              {client.status === 'active' ? (
                                <button
                                  onClick={() => handleBlockUser(client.id, 'block')}
                                  className="text-red-400 hover:text-red-300 font-medium"
                                >
                                  ⛔ Bloquear
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleBlockUser(client.id, 'activate')}
                                  className="text-green-400 hover:text-green-300 font-medium"
                                >
                                  🔄 Reativar
                                </button>
                              )}
                            </>
                          ) : (
                            <span className="text-red-400 font-bold">🔐 ADMMASTER</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TRANSAÇÕES TAB */}
        {!loading && activeTab === 'transactions' && (
          <div className="space-y-6">
            {/* Filtro */}
            <div className="bg-gray-800 rounded-lg shadow-xl p-4 border border-gray-700">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full md:w-64 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-red-500"
              >
                <option value="all">Todas as Transações</option>
                <option value="pending">Pendentes</option>
                <option value="approved">Aprovadas</option>
                <option value="rejected">Rejeitadas</option>
              </select>
              <p className="text-gray-400 text-sm mt-2">
                Mostrando {filteredTransactions.length} transações
              </p>
            </div>

            {/* Tabela */}
            <div className="bg-gray-800 rounded-lg shadow-xl overflow-hidden border border-gray-700">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase">ID</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase">Cliente</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase">Plano</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase">Valor</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase">Método</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center">
                          <div className="text-gray-400">
                            <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg font-semibold mb-2">Nenhuma transação encontrada</p>
                            <p className="text-sm">
                              {transactions.length === 0 
                                ? "Ainda não há pagamentos no sistema. As transações aparecerão aqui assim que forem criadas."
                                : "Nenhuma transação corresponde aos filtros selecionados."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : filteredTransactions.map((txn) => (
                      <tr key={txn.id} className="hover:bg-gray-750">
                        <td className="px-6 py-4 text-xs font-mono text-gray-400">
                          {txn.id.substring(0, 8)}...
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300">{txn.user_email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${getPlanBadge(txn.plan)}`}>
                            {txn.plan}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-green-400">
                          R$ {txn.amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300">{txn.payment_method}</td>
                        <td className="px-6 py-4">{getStatusBadge(txn.status)}</td>
                        <td className="px-6 py-4">
                          {txn.status === 'pending' && (
                            <button
                              onClick={() => {
                                setSelectedTransaction(txn);
                                setShowApprovalModal(true);
                              }}
                              className="text-green-400 hover:text-green-300 font-bold"
                            >
                              ✅ Aprovar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* LOGS TAB */}
        {!loading && activeTab === 'logs' && (
          <div className="space-y-6">
            {/* Header com Exportação */}
            <div className="bg-gray-800 rounded-lg shadow-xl p-4 border border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">📋 Logs e Auditoria</h3>
              <button
                onClick={exportLogs}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <Download className="w-4 h-4" />
                Exportar Logs
              </button>
            </div>

            {/* Lista de Logs */}
            <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 max-h-96 overflow-y-auto">
              {logs.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  Nenhum log registrado ainda
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {logs.map((log, index) => (
                    <div key={index} className="p-4 hover:bg-gray-750">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-blue-900 text-blue-300 text-xs rounded">
                              {log.action}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(log.timestamp).toLocaleString('pt-BR')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-300">
                            {log.target_user_email && `Usuário: ${log.target_user_email}`}
                          </p>
                          {log.details && (
                            <pre className="mt-2 text-xs text-gray-400 bg-gray-900 p-2 rounded overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* FERRAMENTAS TAB */}
        {!loading && activeTab === 'tools' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <TestTube className="w-6 h-6" />
                Ferramentas de Teste
              </h3>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/backups')}
                  className="w-full bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition text-left"
                >
                  💾 Sistema de Backup
                </button>
                <button 
                  onClick={testMercadoPago}
                  disabled={testingInProgress}
                  className={`w-full p-3 rounded-lg transition text-left ${
                    testingInProgress 
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  🔌 Testar Mercado Pago
                </button>
                <button 
                  onClick={testBoleto}
                  disabled={testingInProgress}
                  className={`w-full p-3 rounded-lg transition text-left ${
                    testingInProgress 
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  📄 Testar Geração de Boletos
                </button>
                <button 
                  onClick={testPIX}
                  disabled={testingInProgress}
                  className={`w-full p-3 rounded-lg transition text-left ${
                    testingInProgress 
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  💳 Testar QR Code PIX
                </button>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Settings className="w-6 h-6" />
                Configurações do Sistema
              </h3>
              <div className="space-y-3">
                <button 
                  onClick={() => openConfig('plans')}
                  className="w-full bg-indigo-600 text-white p-3 rounded-lg hover:bg-indigo-700 transition text-left"
                >
                  ⚙️ Gerenciar Planos
                </button>
                <button 
                  onClick={() => openConfig('payment-intents')}
                  className="w-full bg-pink-600 text-white p-3 rounded-lg hover:bg-pink-700 transition text-left font-semibold"
                >
                  💰 Cobranças / Pagamentos
                </button>
                <button 
                  onClick={() => openConfig('bank-credentials')}
                  className="w-full bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 transition text-left"
                >
                  🏦 Credenciais Bancárias
                </button>
                <button 
                  onClick={() => openConfig('payment-providers')}
                  className="w-full bg-teal-600 text-white p-3 rounded-lg hover:bg-teal-700 transition text-left"
                >
                  💳 Provedores de Pagamento
                </button>
                <button 
                  onClick={() => openConfig('webhooks-logs')}
                  className="w-full bg-orange-600 text-white p-3 rounded-lg hover:bg-orange-700 transition text-left"
                >
                  📊 Webhooks & Logs
                </button>
                <button 
                  onClick={() => openConfig('security')}
                  className="w-full bg-red-600 text-white p-3 rounded-lg hover:bg-red-700 transition text-left"
                >
                  🔐 Configurações de Segurança
                </button>
                <button 
                  onClick={() => openConfig('email')}
                  className="w-full bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition text-left"
                >
                  📧 Configurar E-mails
                </button>
                <button 
                  onClick={() => openConfig('seo')}
                  className="w-full bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 transition text-left"
                >
                  🌐 Configurar SEO
                </button>
              </div>
            </div>

            {/* Painel de Resultados de Testes */}
            {testResults && (
              <div className="mt-6 bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    {testResults.success ? '✅' : '❌'} Resultado do Teste
                  </h3>
                  <button
                    onClick={() => setTestResults(null)}
                    className="text-gray-400 hover:text-white text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className={`p-4 rounded-lg ${testResults.success ? 'bg-green-900/30 border border-green-500' : 'bg-red-900/30 border border-red-500'}`}>
                  <p className={`font-bold mb-2 ${testResults.success ? 'text-green-300' : 'text-red-300'}`}>
                    {testResults.message}
                  </p>
                  
                  {testResults.data && (
                    <div className="mt-3 space-y-2 text-sm text-gray-300">
                      {Object.entries(testResults.data).map(([key, value]) => (
                        <p key={key}>
                          <span className="font-semibold">{key}:</span> {value}
                        </p>
                      ))}
                    </div>
                  )}
                  
                  {testResults.error && (
                    <div className="mt-3 bg-gray-900 p-3 rounded text-sm text-red-300">
                      <p className="font-bold mb-1">Erro:</p>
                      <p>{testResults.error}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MODALS */}

        {/* Aba de Atualização do Sistema */}
        {!loading && activeTab === 'system-update' && (
          <div className="space-y-6">
            {/* Informações da Versão Atual */}
            {systemVersion && (
              <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <RefreshCw className="w-6 h-6" />
                  Versão Atual do Sistema
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Versão</p>
                    <p className="text-white text-xl font-bold">{systemVersion.version?.version}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">DB Version</p>
                    <p className="text-white text-xl font-bold">{systemVersion.version?.db_version}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Build Date</p>
                    <p className="text-white text-xl font-bold">{systemVersion.version?.build_date}</p>
                  </div>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm mb-2">Status das Migrações</p>
                  <div className="flex items-center gap-2">
                    {systemVersion.migrations?.up_to_date ? (
                      <span className="flex items-center gap-2 text-green-400 font-semibold">
                        <CheckCircle className="w-5 h-5" />
                        Sistema 100% Atualizado
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-yellow-400 font-semibold">
                        <AlertCircle className="w-5 h-5" />
                        {systemVersion.migrations?.pending_count} Migrações Pendentes
                      </span>
                    )}
                  </div>
                  <p className="text-gray-300 text-sm mt-2">
                    Total: {systemVersion.migrations?.total_migrations} | 
                    Aplicadas: {systemVersion.migrations?.applied_count}
                  </p>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg mt-4">
                  <p className="text-gray-400 text-sm mb-2">Sistema</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">CPU</p>
                      <p className="text-white font-semibold">{systemVersion.system?.cpu_usage}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Memória</p>
                      <p className="text-white font-semibold">{systemVersion.system?.memory_usage}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Disco</p>
                      <p className="text-white font-semibold">{systemVersion.system?.disk_usage}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Upload de Atualização */}
            <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Download className="w-6 h-6" />
                Carregar Arquivo de Atualização
              </h3>

              {!uploadedPackage && (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
                    <input
                      type="file"
                      accept=".zip"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="update-file-input"
                    />
                    <label
                      htmlFor="update-file-input"
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col items-center gap-3">
                        <Download className="w-12 h-12 text-gray-400" />
                        <p className="text-white font-semibold">
                          {updateFile ? updateFile.name : 'Clique para selecionar arquivo ZIP'}
                        </p>
                        <p className="text-gray-400 text-sm">
                          Arquivo de atualização deve ser um pacote ZIP válido
                        </p>
                      </div>
                    </label>
                  </div>

                  {updateFile && (
                    <div className="flex gap-3">
                      <button
                        onClick={handleUploadPackage}
                        className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold"
                      >
                        📤 Validar Pacote
                      </button>
                      <button
                        onClick={handleCancelUpload}
                        className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-500 transition"
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              )}

              {uploadedPackage && (
                <div className="space-y-4">
                  <div className="bg-green-900/30 border border-green-500 rounded-lg p-4">
                    <p className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Pacote Validado com Sucesso!
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-400">Versão</p>
                        <p className="text-white font-semibold">{uploadedPackage.validation?.info?.version}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Tipo</p>
                        <p className="text-white font-semibold uppercase">{uploadedPackage.validation?.info?.type}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Tamanho</p>
                        <p className="text-white font-semibold">{(uploadedPackage.validation?.info?.size / 1024).toFixed(2)} KB</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Arquivos</p>
                        <p className="text-white font-semibold">{uploadedPackage.validation?.info?.files_count}</p>
                      </div>
                    </div>

                    <div className="mt-3 flex gap-2 text-sm">
                      {uploadedPackage.validation?.info?.has_backend && (
                        <span className="bg-blue-600 px-2 py-1 rounded">⚙️ Backend</span>
                      )}
                      {uploadedPackage.validation?.info?.has_frontend && (
                        <span className="bg-purple-600 px-2 py-1 rounded">🎨 Frontend</span>
                      )}
                      {uploadedPackage.validation?.info?.has_migrations && (
                        <span className="bg-green-600 px-2 py-1 rounded">💾 Migrações</span>
                      )}
                      {uploadedPackage.validation?.info?.has_scripts && (
                        <span className="bg-orange-600 px-2 py-1 rounded">📜 Scripts</span>
                      )}
                    </div>

                    <p className="text-gray-300 mt-3">
                      <strong>Descrição:</strong> {uploadedPackage.validation?.info?.description}
                    </p>

                    {uploadedPackage.validation?.warnings && uploadedPackage.validation.warnings.length > 0 && (
                      <div className="mt-3 bg-yellow-900/30 border border-yellow-500 rounded p-3">
                        <p className="text-yellow-400 font-semibold text-sm mb-1">⚠️ Avisos:</p>
                        {uploadedPackage.validation.warnings.map((warning, idx) => (
                          <p key={idx} className="text-yellow-300 text-sm">• {warning}</p>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleApplyUpdate}
                      disabled={applyingUpdate}
                      className={`flex-1 py-3 rounded-lg transition font-semibold ${
                        applyingUpdate
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {applyingUpdate ? '⏳ Aplicando...' : '🚀 Aplicar Atualização'}
                    </button>
                    <button
                      onClick={handleCancelUpload}
                      disabled={applyingUpdate}
                      className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancelar
                    </button>
                  </div>

                  <div className="bg-red-900/30 border border-red-500 rounded-lg p-4">
                    <p className="text-red-400 font-semibold text-sm">
                      ⚠️ ATENÇÃO: Esta operação irá atualizar TODO O SISTEMA
                    </p>
                    <p className="text-gray-300 text-sm mt-2">
                      • Um backup automático será criado antes da atualização<br />
                      • Backend, frontend e banco de dados serão atualizados<br />
                      • O sistema poderá requerer reinicialização após a atualização
                    </p>
                  </div>
                </div>
              )}

              {/* Progresso da Atualização */}
              {updateProgress.length > 0 && (
                <div className="mt-4 bg-gray-700 rounded-lg p-4">
                  <p className="text-white font-semibold mb-2">📊 Progresso da Atualização:</p>
                  <div className="space-y-1 text-sm max-h-60 overflow-y-auto">
                    {updateProgress.map((step, idx) => (
                      <p key={idx} className="text-gray-300">{step}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Histórico de Atualizações */}
            {updateHistory.length > 0 && (
              <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-6 h-6" />
                  Histórico de Atualizações
                </h3>

                <div className="space-y-3">
                  {updateHistory.slice(0, 5).map((update, idx) => (
                    <div key={idx} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-white font-semibold text-lg">
                              v{update.version}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs uppercase font-semibold ${
                              update.type === 'major' ? 'bg-red-600' :
                              update.type === 'update' ? 'bg-blue-600' :
                              update.type === 'hotfix' ? 'bg-orange-600' :
                              'bg-green-600'
                            }`}>
                              {update.type}
                            </span>
                          </div>
                          
                          <p className="text-gray-300 text-sm mb-2">{update.description}</p>
                          
                          <div className="flex items-center gap-4 text-xs text-gray-400">
                            <span>📅 {new Date(update.applied_at).toLocaleString('pt-BR')}</span>
                            {update.backup_created && (
                              <span>💾 Backup: {update.backup_created}</span>
                            )}
                          </div>

                          {update.errors && update.errors.length > 0 && (
                            <div className="mt-2 text-red-400 text-xs">
                              ⚠️ {update.errors.length} erro(s) durante atualização
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sistema de Rollback - Reverter Atualização */}
            <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-orange-600">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <RefreshCw className="w-6 h-6 text-orange-400" />
                ↩️ Reverter Atualização (Rollback)
              </h3>

              <div className="bg-orange-900/30 border border-orange-500 rounded-lg p-4 mb-4">
                <p className="text-orange-400 font-semibold text-sm mb-2">
                  ⚠️ FERRAMENTA DE EMERGÊNCIA
                </p>
                <p className="text-gray-300 text-sm">
                  Use esta função APENAS se uma atualização causou problemas no sistema.
                  Isso irá restaurar o sistema para uma versão anterior usando os backups automáticos.
                </p>
              </div>

              {rollbackBackups.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-gray-400 text-sm mb-3">
                    Backups disponíveis para reversão ({rollbackBackups.length}):
                  </p>
                  
                  {rollbackBackups.map((backup, idx) => (
                    <div key={idx} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-white font-semibold">
                              📦 {backup.name || backup.backup_name}
                            </span>
                            {idx === 0 && (
                              <span className="bg-green-600 px-2 py-1 rounded text-xs">
                                Mais Recente
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs text-gray-400 mb-2">
                            <div>
                              <span className="text-gray-500">Criado:</span> {new Date(backup.created_at).toLocaleString('pt-BR')}
                            </div>
                            <div>
                              <span className="text-gray-500">Tamanho:</span> {((backup.size || 0) / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>

                          {backup.version && (
                            <p className="text-gray-400 text-xs">
                              Versão do sistema no backup: <span className="text-white">{backup.version}</span>
                            </p>
                          )}
                        </div>

                        <button
                          onClick={() => handleRollback(backup.name || backup.backup_name)}
                          disabled={performingRollback}
                          className={`ml-4 px-4 py-2 rounded-lg font-semibold transition ${
                            performingRollback
                              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                              : 'bg-orange-600 text-white hover:bg-orange-700'
                          }`}
                        >
                          {performingRollback ? '⏳ Revertendo...' : '↩️ Reverter'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-700 rounded-lg p-6 text-center">
                  <p className="text-gray-400">
                    Nenhum backup de pré-atualização disponível no momento.
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Backups são criados automaticamente antes de cada atualização.
                  </p>
                </div>
              )}

              {/* Progresso do Rollback */}
              {rollbackProgress.length > 0 && (
                <div className="mt-4 bg-gray-700 rounded-lg p-4 border border-orange-500">
                  <p className="text-white font-semibold mb-2">📊 Progresso da Reversão:</p>
                  <div className="space-y-1 text-sm max-h-60 overflow-y-auto">
                    {rollbackProgress.map((step, idx) => (
                      <p key={idx} className="text-gray-300">{step}</p>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 bg-red-900/30 border border-red-500 rounded-lg p-4">
                <p className="text-red-400 font-semibold text-sm mb-2">
                  ⚠️ ATENÇÃO IMPORTANTE:
                </p>
                <p className="text-gray-300 text-xs space-y-1">
                  • A reversão restaura TODOS os arquivos do backup<br />
                  • Alterações feitas após a atualização serão PERDIDAS<br />
                  • O sistema REQUER reinicialização após o rollback<br />
                  • Use esta função apenas em situações de emergência
                </p>
              </div>
            </div>
          </div>
        )}

      {showApprovalModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 border-red-500">
            <h3 className="text-xl font-bold text-white mb-4">✅ Aprovar Pagamento</h3>
            <div className="space-y-3 mb-6 text-gray-300">
              <p><strong>Cliente:</strong> {selectedTransaction.user_email}</p>
              <p><strong>Plano:</strong> {selectedTransaction.plan}</p>
              <p><strong>Valor:</strong> R$ {selectedTransaction.amount.toFixed(2)}</p>
              <p><strong>Método:</strong> {selectedTransaction.payment_method}</p>
            </div>
            <div className="bg-yellow-900 border-l-4 border-yellow-500 p-4 mb-6">
              <p className="text-sm text-yellow-200">
                ⚠️ Esta ação liberará o plano imediatamente
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleApprovePayment(selectedTransaction.id)}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition font-bold"
              >
                ✅ Confirmar
              </button>
              <button
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-500 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showPlanModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 border-red-500">
            <h3 className="text-xl font-bold text-white mb-4">🔓 Liberar Plano Manualmente</h3>
            <div className="space-y-2 mb-6 text-gray-300">
              <p><strong>Cliente:</strong> {selectedClient.name}</p>
              <p><strong>Email:</strong> {selectedClient.email}</p>
              <p><strong>Plano Atual:</strong> {selectedClient.plan}</p>
            </div>
            <div className="space-y-3 mb-6">
              <button
                onClick={() => handleUpdatePlan(selectedClient.id, 'professional')}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold"
              >
                Professional (10 músicas) - R$ 99,90
              </button>
              <button
                onClick={() => handleUpdatePlan(selectedClient.id, 'premium')}
                className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition font-semibold"
              >
                Premium (6 meses) - R$ 199,90
              </button>
              <button
                onClick={() => handleUpdatePlan(selectedClient.id, 'annual')}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition font-semibold"
              >
                Annual (12 meses) - R$ 299,90
              </button>
            </div>
            <button
              onClick={() => setShowPlanModal(false)}
              className="w-full bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-500 transition"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {showClientDetailsModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full border-2 border-blue-500 max-h-96 overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">👁️ Detalhes do Cliente</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Nome:</p>
                <p className="text-white font-semibold">{selectedClient.name}</p>
              </div>
              <div>
                <p className="text-gray-400">Email:</p>
                <p className="text-white font-semibold">{selectedClient.email}</p>
              </div>
              <div>
                <p className="text-gray-400">ID:</p>
                <p className="text-white font-mono text-xs">{selectedClient.id}</p>
              </div>
              <div>
                <p className="text-gray-400">Plano:</p>
                <span className={`px-2 py-1 text-xs rounded-full ${getPlanBadge(selectedClient.plan)}`}>
                  {selectedClient.plan}
                </span>
              </div>
              <div>
                <p className="text-gray-400">Obras Cadastradas:</p>
                <p className="text-white font-semibold">{selectedClient.works_count}</p>
              </div>
              <div>
                <p className="text-gray-400">Status:</p>
                {getStatusBadge(selectedClient.status)}
              </div>
              <div>
                <p className="text-gray-400">Data de Cadastro:</p>
                <p className="text-white">{new Date(selectedClient.created_at).toLocaleDateString('pt-BR')}</p>
              </div>
              <div>
                <p className="text-gray-400">Tipo de Conta:</p>
                <p className="text-white">{selectedClient.is_master ? '🔐 MASTER' : '👤 Cliente'}</p>
              </div>
            </div>
            <button
              onClick={() => setShowClientDetailsModal(false)}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition mt-6"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIGURAÇÕES */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full border-2 border-blue-500 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                {configType === 'plans' && '⚙️ Gerenciar Planos'}
                {configType === 'payment-intents' && '💰 Cobranças / Pagamentos'}
                {configType === 'bank-credentials' && '🏦 Credenciais Bancárias'}
                {configType === 'payment-providers' && '💳 Provedores de Pagamento'}
                {configType === 'webhooks-logs' && '📊 Webhooks & Logs'}
                {configType === 'security' && '🔐 Configurações de Segurança'}
                {configType === 'email' && '📧 Configurar E-mails'}
                {configType === 'seo' && '🌐 Configurar SEO'}
              </h3>
              <button
                onClick={() => setShowConfigModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            {/* Conteúdo baseado no tipo */}
            <div className="text-gray-300 space-y-4">
              {configType === 'plans' && editablePlans && (
                <div className="space-y-6">
                  <p className="text-gray-400">Configure os planos disponíveis no sistema:</p>
                  
                  {/* Plano Individual / Gratuito */}
                  <div className="bg-gray-700 p-4 rounded-lg border-2 border-green-600">
                    <h4 className="font-bold text-white mb-4">📦 Plano Gratuito (Individual)</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-sm text-gray-400">Nome do Plano</label>
                        <input
                          type="text"
                          value={editablePlans.individual?.name || 'Plano Individual'}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            individual: {...editablePlans.individual, name: e.target.value}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: Plano Gratuito"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Preço (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editablePlans.individual?.price || 0}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            individual: {...editablePlans.individual, price: parseFloat(e.target.value)}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">R$ 0 = Sempre gratuito</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Limite de Registros/Obras</label>
                        <input
                          type="number"
                          value={editablePlans.individual?.music_limit || 5}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            individual: {...editablePlans.individual, music_limit: parseInt(e.target.value)}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: 5"
                        />
                        <p className="text-xs text-gray-500 mt-1">Número máximo de obras</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Duração (meses)</label>
                        <input
                          type="number"
                          value={editablePlans?.individual?.duration_months ?? 6}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            individual: {...editablePlans.individual, duration_months: parseInt(e.target.value) || 6}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">0 = Sempre ativo</p>
                      </div>
                    </div>
                    <div className="bg-green-900/30 p-2 rounded border border-green-600">
                      <p className="text-xs text-green-300">
                        ℹ️ Este é o plano gratuito. Usuários se registram automaticamente neste plano.
                      </p>
                    </div>
                  </div>

                  {/* Plano Professional */}
                  <div className="bg-gray-700 p-4 rounded-lg border-2 border-blue-600">
                    <h4 className="font-bold text-white mb-4">💼 Plano Professional</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="col-span-2">
                        <label className="text-sm text-gray-400">Nome do Plano</label>
                        <input
                          type="text"
                          value={editablePlans.professional?.name || 'Plano Professional'}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            professional: {...editablePlans.professional, name: e.target.value}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: Plano Professional"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Preço (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editablePlans.professional?.price || 99.90}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            professional: {...editablePlans.professional, price: parseFloat(e.target.value)}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: 99.90"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Limite de Registros/Obras</label>
                        <input
                          type="number"
                          value={editablePlans.professional?.music_limit || 50}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            professional: {...editablePlans.professional, music_limit: parseInt(e.target.value)}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: 50"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Duração (meses)</label>
                        <input
                          type="number"
                          value={editablePlans?.professional?.duration_months ?? 6}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            professional: {...editablePlans.professional, duration_months: parseInt(e.target.value) || 6}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: 6"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Plano Premium */}
                  <div className="bg-gray-700 p-4 rounded-lg border-2 border-purple-600">
                    <h4 className="font-bold text-white mb-4">👑 Plano Premium</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="col-span-2">
                        <label className="text-sm text-gray-400">Nome do Plano</label>
                        <input
                          type="text"
                          value={editablePlans.premium?.name || 'Plano Premium'}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            premium: {...editablePlans.premium, name: e.target.value}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: Plano Premium"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Preço (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editablePlans.premium?.price || 299.90}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            premium: {...editablePlans.premium, price: parseFloat(e.target.value)}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: 299.90"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Limite de Registros/Obras</label>
                        <input
                          type="number"
                          value={editablePlans.premium?.music_limit || -1}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            premium: {...editablePlans.premium, music_limit: parseInt(e.target.value)}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">-1 = Ilimitado</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Duração (meses)</label>
                        <input
                          type="number"
                          value={editablePlans?.premium?.duration_months ?? 6}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            premium: {...editablePlans.premium, duration_months: parseInt(e.target.value) || 6}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: 6"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Plano Annual */}
                  <div className="bg-gray-700 p-4 rounded-lg border-2 border-yellow-600">
                    <h4 className="font-bold text-white mb-4">⭐ Plano Anual (Annual)</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="col-span-2">
                        <label className="text-sm text-gray-400">Nome do Plano</label>
                        <input
                          type="text"
                          value={editablePlans.annual?.name || 'Plano Anual'}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            annual: {...editablePlans.annual, name: e.target.value}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: Plano Anual ILIMITADO"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Preço (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editablePlans.annual?.price || 299.90}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            annual: {...editablePlans.annual, price: parseFloat(e.target.value)}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: 299.90"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Limite de Registros/Obras</label>
                        <input
                          type="number"
                          value={editablePlans.annual?.music_limit || -1}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            annual: {...editablePlans.annual, music_limit: parseInt(e.target.value)}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">-1 = Ilimitado</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Duração (meses)</label>
                        <input
                          type="number"
                          value={editablePlans?.annual?.duration_months ?? 12}
                          onChange={(e) => setEditablePlans({
                            ...editablePlans,
                            annual: {...editablePlans.annual, duration_months: parseInt(e.target.value) || 12}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="Ex: 12"
                        />
                        <p className="text-xs text-gray-500 mt-1">Padrão: 12 meses (anual)</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-900/30 p-4 rounded-lg border border-green-500">
                    <p className="text-sm text-green-200">
                      ✅ As alterações serão salvas e aplicadas imediatamente ao clicar em "Salvar Configurações"
                    </p>
                  </div>
                </div>
              )}

              {/* Payment Intents / Cobranças Content */}
              {configType === 'payment-intents' && (
                <div className="space-y-4">
                  {/* Stats Cards */}
                  {paymentStats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="bg-blue-900 p-3 rounded-lg border border-blue-500">
                        <p className="text-xs text-blue-300">Total</p>
                        <p className="text-2xl font-bold text-white">{paymentStats.total_intents}</p>
                      </div>
                      <div className="bg-green-900 p-3 rounded-lg border border-green-500">
                        <p className="text-xs text-green-300">Aprovadas</p>
                        <p className="text-2xl font-bold text-white">{paymentStats.approved_count}</p>
                      </div>
                      <div className="bg-yellow-900 p-3 rounded-lg border border-yellow-500">
                        <p className="text-xs text-yellow-300">Pendentes</p>
                        <p className="text-2xl font-bold text-white">{paymentStats.pending_count}</p>
                      </div>
                      <div className="bg-purple-900 p-3 rounded-lg border border-purple-500">
                        <p className="text-xs text-purple-300">Valor Total</p>
                        <p className="text-xl font-bold text-white">R$ {paymentStats.total_amount?.toFixed(2)}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Filters */}
                  <div className="bg-gray-700 p-3 rounded-lg">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Período</label>
                        <select
                          value={intentFilter.days}
                          onChange={(e) => {
                            setIntentFilter({...intentFilter, days: parseInt(e.target.value)});
                            setTimeout(() => openConfig('payment-intents'), 100);
                          }}
                          className="w-full bg-gray-800 text-white px-2 py-1 rounded text-sm"
                        >
                          <option value="1">Hoje</option>
                          <option value="7">7 dias</option>
                          <option value="30">30 dias</option>
                          <option value="90">90 dias</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Método</label>
                        <select
                          value={intentFilter.method}
                          onChange={(e) => setIntentFilter({...intentFilter, method: e.target.value})}
                          className="w-full bg-gray-800 text-white px-2 py-1 rounded text-sm"
                        >
                          <option value="all">Todos</option>
                          <option value="pix">PIX</option>
                          <option value="boleto">Boleto</option>
                          <option value="manual">Manual</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Status</label>
                        <select
                          value={intentFilter.status}
                          onChange={(e) => setIntentFilter({...intentFilter, status: e.target.value})}
                          className="w-full bg-gray-800 text-white px-2 py-1 rounded text-sm"
                        >
                          <option value="all">Todos</option>
                          <option value="approved">Aprovado</option>
                          <option value="pending">Pendente</option>
                          <option value="created">Criado</option>
                          <option value="expired">Expirado</option>
                          <option value="rejected">Rejeitado</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Intents List */}
                  {loadingCredentials ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
                      <p className="text-gray-400 mt-2">Carregando cobranças...</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {paymentIntents
                        .filter(intent => 
                          (intentFilter.method === 'all' || intent.payment_method === intentFilter.method) &&
                          (intentFilter.status === 'all' || intent.status === intentFilter.status)
                        )
                        .map((intent) => (
                        <div
                          key={intent.id}
                          className="bg-gray-700 p-3 rounded-lg border border-gray-600 hover:border-blue-500 transition cursor-pointer"
                          onClick={() => setSelectedIntent(intent)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                                intent.status === 'approved' ? 'bg-green-600' :
                                intent.status === 'pending' || intent.status === 'created' ? 'bg-yellow-600' :
                                intent.status === 'expired' ? 'bg-gray-600' :
                                'bg-red-600'
                              }`}>
                                {intent.status.toUpperCase()}
                              </span>
                              <span className="px-2 py-1 text-xs rounded-full bg-blue-600">
                                {intent.payment_method.toUpperCase()}
                              </span>
                            </div>
                            <span className="text-lg font-bold text-white">
                              R$ {intent.amount?.toFixed(2)}
                            </span>
                          </div>
                          
                          <div className="text-sm space-y-1">
                            {intent.user_name && (
                              <p className="font-bold text-white text-base">
                                👤 {intent.user_name}
                              </p>
                            )}
                            <p className="text-gray-300">📧 {intent.user_email}</p>
                            <p className="text-gray-300">📦 {intent.plan} - {intent.description}</p>
                            {intent.transaction_id && (
                              <div className="bg-blue-900/40 border border-blue-500/50 px-3 py-2 rounded mt-2">
                                <p className="text-xs text-blue-200 font-semibold mb-1">ID de Transação:</p>
                                <p className="font-mono text-sm text-white select-all">
                                  {intent.transaction_id}
                                </p>
                              </div>
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              🕐 {new Date(intent.created_at).toLocaleString('pt-BR')}
                            </p>
                          </div>
                          
                          {/* Action Buttons */}
                          {intent.status !== 'approved' && (
                            <div className="flex gap-2 mt-2 pt-2 border-t border-gray-600">
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await axios.post(
                                      `${API_URL}/admin/payment-intents/${intent.id}/approve-manual`,
                                      { notes: 'Aprovado manualmente pelo ADMMASTER' },
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success('Pagamento aprovado!');
                                    openConfig('payment-intents');
                                  } catch (error) {
                                    toast.error('Erro ao aprovar');
                                  }
                                }}
                                className="flex-1 bg-green-600 px-3 py-1 rounded text-xs hover:bg-green-700 transition"
                              >
                                ✓ Aprovar Manual
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const response = await axios.post(
                                      `${API_URL}/admin/payment-intents/${intent.id}/sync`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success(response.data.message || 'Sincronizado!');
                                    openConfig('payment-intents');
                                  } catch (error) {
                                    toast.error('Erro ao sincronizar');
                                  }
                                }}
                                className="flex-1 bg-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-700 transition"
                              >
                                🔄 Sync Provedor
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {paymentIntents.filter(intent => 
                        (intentFilter.method === 'all' || intent.payment_method === intentFilter.method) &&
                        (intentFilter.status === 'all' || intent.status === intentFilter.status)
                      ).length === 0 && (
                        <div className="bg-gray-700 p-8 rounded-lg text-center">
                          <p className="text-gray-400">Nenhuma cobrança encontrada com os filtros selecionados</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="bg-blue-900 p-4 rounded-lg border border-blue-500 mt-4">
                    <p className="text-sm text-blue-200">
                      💡 <strong>Dica:</strong> Clique em uma cobrança para ver detalhes completos. 
                      Use "Aprovar Manual" apenas para pagamentos confirmados fora do sistema.
                    </p>
                  </div>
                </div>
              )}

              {/* Bank Credentials Content */}
              {configType === 'bank-credentials' && (
                <div className="space-y-4">
                  {!showCredentialForm ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-gray-400">Gerencie credenciais bancárias para pagamentos</p>
                        <button
                          onClick={() => setShowCredentialForm(true)}
                          className="bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                        >
                          ➕ Nova Credencial
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-white">
                          {editingCredential ? 'Editar Credencial Bancária' : 'Nova Credencial Bancária'}
                        </h4>
                        <button
                          onClick={() => {
                            setShowCredentialForm(false);
                            setEditingCredential(null);
                          }}
                          className="text-gray-400 hover:text-white"
                        >
                          ✕ Fechar
                        </button>
                      </div>
                      
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        const formData = new FormData(e.target);
                        const data = {
                          credential_name: formData.get('credential_name'),
                          credential_type: formData.get('credential_type'),
                          bank_name: formData.get('bank_name') || null,
                          bank_code: formData.get('bank_code') || null,
                          agency: formData.get('agency') || null,
                          account: formData.get('account') || null,
                          account_digit: formData.get('account_digit') || null,
                          pix_key_type: formData.get('pix_key_type') || null,
                          pix_key: formData.get('pix_key') || null,
                          api_client_id: formData.get('api_client_id') || null,
                          api_client_secret: formData.get('api_client_secret') || null,
                          api_access_token: formData.get('api_access_token') || null,
                          api_public_key: formData.get('api_public_key') || null,
                          api_environment: formData.get('api_environment') || 'production',
                          beneficiary_name: formData.get('beneficiary_name') || null,
                          beneficiary_document: formData.get('beneficiary_document') || null,
                          notes: formData.get('notes') || null,
                          is_active: formData.get('is_active') === 'on'
                        };
                        
                        try {
                          if (editingCredential) {
                            // Editar
                            await axios.put(`${API_URL}/admin/bank-credentials/${editingCredential.id}`, data, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Credencial atualizada com sucesso!');
                          } else {
                            // Criar
                            await axios.post(`${API_URL}/admin/bank-credentials`, data, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Credencial criada com sucesso!');
                          }
                          setShowCredentialForm(false);
                          setEditingCredential(null);
                          openConfig('bank-credentials');
                        } catch (error) {
                          toast.error(editingCredential ? 'Erro ao atualizar credencial' : 'Erro ao criar credencial');
                          console.error(error);
                        }
                      }} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Nome da Credencial *</label>
                            <input
                              type="text"
                              name="credential_name"
                              required
                              defaultValue={editingCredential?.credential_name || ''}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                              placeholder="Ex: Santander Principal"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Tipo *</label>
                            <select
                              name="credential_type"
                              required
                              defaultValue={editingCredential?.credential_type || 'boleto'}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                            >
                              <option value="boleto">Boleto</option>
                              <option value="pix">PIX</option>
                              <option value="transfer">Transferência</option>
                              <option value="mercado_pago">Mercado Pago</option>
                              <option value="payment_gateway">Gateway</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Nome do Banco</label>
                            <input
                              type="text"
                              name="bank_name"
                              defaultValue={editingCredential?.bank_name || ''}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                              placeholder="Ex: Santander"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Código do Banco</label>
                            <input
                              type="text"
                              name="bank_code"
                              defaultValue={editingCredential?.bank_code || ''}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                              placeholder="Ex: 033"
                              maxLength="10"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Agência</label>
                            <input
                              type="text"
                              name="agency"
                              defaultValue={editingCredential?.agency || ''}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                              placeholder="0933"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Conta</label>
                            <input
                              type="text"
                              name="account"
                              defaultValue={editingCredential?.account || ''}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                              placeholder="13002933"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Dígito</label>
                            <input
                              type="text"
                              name="account_digit"
                              defaultValue={editingCredential?.account_digit || ''}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                              placeholder="2"
                              maxLength="2"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Tipo Chave PIX</label>
                            <select
                              name="pix_key_type"
                              defaultValue={editingCredential?.pix_key_type || 'cpf'}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                            >
                              <option value="cpf">CPF</option>
                              <option value="cnpj">CNPJ</option>
                              <option value="email">Email</option>
                              <option value="telefone">Telefone</option>
                              <option value="aleatoria">Aleatória</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Chave PIX</label>
                            <input
                              type="text"
                              name="pix_key"
                              defaultValue={editingCredential?.pix_key || ''}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                              placeholder="38.256.795/0001-76"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Client ID / API Key</label>
                            <input
                              type="text"
                              name="api_client_id"
                              defaultValue={editingCredential?.api_client_id || ''}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                              placeholder="Client ID"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Client Secret / Token</label>
                            <input
                              type="password"
                              name="api_client_secret"
                              defaultValue={editingCredential?.api_client_secret || ''}
                              className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                              placeholder="••••••••••"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">Observações</label>
                          <textarea
                            name="notes"
                            rows="2"
                            defaultValue={editingCredential?.notes || ''}
                            className="w-full bg-gray-800 text-white px-3 py-2 rounded text-sm"
                            placeholder="Observações adicionais..."
                          />
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            name="is_active"
                            id="is_active"
                            defaultChecked={editingCredential?.is_active || false}
                            className="w-4 h-4"
                          />
                          <label htmlFor="is_active" className="text-sm text-white">
                            Ativar esta credencial (desativa outras do mesmo tipo)
                          </label>
                        </div>
                        
                        <div className="flex gap-2 pt-3 border-t border-gray-600">
                          <button
                            type="button"
                            onClick={() => {
                              setShowCredentialForm(false);
                              setEditingCredential(null);
                            }}
                            className="flex-1 bg-gray-600 px-4 py-2 rounded hover:bg-gray-500 transition"
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            className="flex-1 bg-blue-600 px-4 py-2 rounded hover:bg-blue-700 transition"
                          >
                            💾 Salvar Credencial
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                  
                  {!showCredentialForm && loadingCredentials ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
                      <p className="text-gray-400 mt-2">Carregando...</p>
                    </div>
                  ) : !showCredentialForm && bankCredentials.length === 0 ? (
                    <div className="bg-gray-700 p-8 rounded-lg text-center">
                      <p className="text-gray-400 mb-4">Nenhuma credencial cadastrada</p>
                      <button
                        onClick={() => setShowCredentialForm(true)}
                        className="bg-blue-600 px-6 py-3 rounded-lg hover:bg-blue-700 transition"
                      >
                        Cadastrar Primeira Credencial
                      </button>
                    </div>
                  ) : !showCredentialForm ? (
                    <div className="space-y-3">
                      {bankCredentials.map((cred) => (
                        <div
                          key={cred.id}
                          className={`bg-gray-700 p-4 rounded-lg border-2 ${
                            cred.is_active ? 'border-green-500' : 'border-gray-600'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-bold text-white">{cred.credential_name}</h4>
                                {cred.is_active && (
                                  <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                                    ATIVA
                                  </span>
                                )}
                                <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full uppercase">
                                  {cred.credential_type}
                                </span>
                              </div>
                              <div className="text-sm text-gray-300 space-y-1">
                                {cred.bank_name && <p>🏦 {cred.bank_name} ({cred.bank_code})</p>}
                                {cred.agency && <p>🏢 Agência: {cred.agency}</p>}
                                {cred.account && (
                                  <p>💳 Conta: {cred.account}{cred.account_digit && `-${cred.account_digit}`}</p>
                                )}
                                {cred.pix_key && <p>🔑 PIX: {cred.pix_key}</p>}
                                {cred.notes && <p className="text-gray-400 text-xs mt-2">{cred.notes}</p>}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {/* Botão Ativar */}
                              {!cred.is_active && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.put(
                                        `${API_URL}/admin/bank-credentials/${cred.id}/activate`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success('Credencial ativada!');
                                      openConfig('bank-credentials');
                                    } catch (error) {
                                      toast.error('Erro ao ativar');
                                    }
                                  }}
                                  className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition"
                                  title="Ativar"
                                >
                                  ✓ Ativar
                                </button>
                              )}
                              
                              {/* Botão Desativar */}
                              {cred.is_active && (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm('Desativar esta credencial?')) return;
                                    try {
                                      await axios.put(
                                        `${API_URL}/admin/bank-credentials/${cred.id}/deactivate`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success('Credencial desativada!');
                                      openConfig('bank-credentials');
                                    } catch (error) {
                                      toast.error('Erro ao desativar');
                                    }
                                  }}
                                  className="px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition"
                                  title="Desativar"
                                >
                                  ✗ Desativar
                                </button>
                              )}
                              
                              {/* Botão Editar */}
                              <button
                                onClick={() => {
                                  setEditingCredential(cred);
                                  setShowCredentialForm(true);
                                }}
                                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition"
                                title="Editar"
                              >
                                ✎ Editar
                              </button>
                              
                              {/* Botão Deletar */}
                              {!cred.is_active && (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm('Excluir esta credencial permanentemente?')) return;
                                    try {
                                      await axios.delete(
                                        `${API_URL}/admin/bank-credentials/${cred.id}`,
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success('Credencial excluída!');
                                      openConfig('bank-credentials');
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Erro ao excluir');
                                    }
                                  }}
                                  className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition"
                                  title="Excluir"
                                >
                                  🗑 Excluir
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  
                  {!showCredentialForm && (
                    <div className="bg-blue-900 p-4 rounded-lg border border-blue-500 mt-4">
                      <p className="text-sm text-blue-200">
                        💡 <strong>Dica:</strong> Dados sensíveis (tokens, secrets) são automaticamente criptografados.
                      Apenas uma credencial pode estar ativa por vez para cada tipo.
                    </p>
                  </div>
                  )}
                </div>
              )}

              {/* Payment Providers Content */}
              {configType === 'payment-providers' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">Configure os provedores de pagamento ativos no sistema</p>
                  
                  {loadingCredentials ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
                      <p className="text-gray-400 mt-2">Carregando...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentProviders.map((provider) => (
                        <div
                          key={provider.provider_type}
                          className={`bg-gray-700 p-4 rounded-lg border-2 ${
                            provider.is_active ? 'border-green-500' : 'border-gray-600'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-bold text-white">{provider.display_name}</h4>
                                {provider.is_active && (
                                  <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                                    ATIVO
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-400">{provider.description}</p>
                            </div>
                            <div className="flex gap-2">
                              {/* Botão Ativar */}
                              {!provider.is_active && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.post(
                                        `${API_URL}/admin/payment-providers/${provider.provider_type}/activate`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success(`${provider.display_name} ativado!`);
                                      openConfig('payment-providers');
                                    } catch (error) {
                                      toast.error('Erro ao ativar provedor');
                                    }
                                  }}
                                  className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition"
                                >
                                  ✓ Ativar
                                </button>
                              )}
                              
                              {/* Botão Desativar */}
                              {provider.is_active && (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Desativar ${provider.display_name}?`)) return;
                                    try {
                                      await axios.post(
                                        `${API_URL}/admin/payment-providers/${provider.provider_type}/deactivate`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success(`${provider.display_name} desativado!`);
                                      openConfig('payment-providers');
                                    } catch (error) {
                                      toast.error('Erro ao desativar provedor');
                                    }
                                  }}
                                  className="px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition"
                                >
                                  ✗ Desativar
                                </button>
                              )}
                              
                              {/* Botão Editar/Configurar */}
                              <button
                                onClick={() => {
                                  toast.info(`Configurações de ${provider.display_name} - Em breve!`);
                                }}
                                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition"
                              >
                                ⚙️ Configurar
                              </button>
                            </div>
                          </div>
                          
                          {provider.last_test_date && (
                            <div className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-600">
                              <p>Último teste: {new Date(provider.last_test_date).toLocaleString('pt-BR')}</p>
                              <p className={provider.last_test_status === 'success' ? 'text-green-400' : 'text-red-400'}>
                                Status: {provider.last_test_status === 'success' ? '✓ Sucesso' : '✗ Erro'}
                              </p>
                              {provider.last_test_message && (
                                <p className="mt-1">{provider.last_test_message}</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="bg-yellow-900 p-4 rounded-lg border border-yellow-500 mt-4">
                    <p className="text-sm text-yellow-200">
                      ⚠️ <strong>Atenção:</strong> Apenas um provedor pode estar ativo por tipo de pagamento (PIX/Boleto).
                      Configure as credenciais em "Credenciais Bancárias" antes de ativar os provedores.
                    </p>
                  </div>
                </div>
              )}

              {/* Webhooks & Logs Content */}
              {configType === 'webhooks-logs' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">Visualize logs de webhooks e transações de pagamento</p>
                  
                  {loadingCredentials ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
                      <p className="text-gray-400 mt-2">Carregando logs...</p>
                    </div>
                  ) : webhookLogs.length === 0 ? (
                    <div className="bg-gray-700 p-8 rounded-lg text-center">
                      <p className="text-gray-400">Nenhum log de webhook registrado ainda</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {webhookLogs.map((log, index) => (
                        <div
                          key={log.id || index}
                          className="bg-gray-700 p-3 rounded-lg border border-gray-600 hover:border-blue-500 transition"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${
                                log.processed ? 'bg-green-500' : 'bg-yellow-500'
                              }`}></span>
                              <span className="font-semibold text-white text-sm">
                                {log.webhook_type}
                              </span>
                              <span className="text-xs text-gray-400">
                                {log.event_type}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(log.created_at).toLocaleString('pt-BR')}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">
                            <p>Status HTTP: {log.status_code}</p>
                            <p>Processado: {log.processed ? '✓ Sim' : '✗ Não'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="bg-blue-900 p-4 rounded-lg border border-blue-500 mt-4">
                    <p className="text-sm text-blue-200">
                      ℹ️ Os webhooks são processados automaticamente quando recebidos.
                      Logs ficam disponíveis para auditoria e troubleshooting.
                    </p>
                  </div>
                </div>
              )}

              {configType === 'security' && (
                <div className="space-y-4">
                  <p className="text-gray-400">Configurações de segurança do sistema:</p>
                  
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-white mb-2">🔑 Autenticação</h4>
                    <div className="space-y-2 text-sm">
                      <p>✅ JWT Tokens configurados</p>
                      <p>✅ Senha hasheada com bcrypt</p>
                      <p>✅ Email case-insensitive</p>
                    </div>
                  </div>

                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-white mb-2">🛡️ Proteções Ativas</h4>
                    <div className="space-y-2 text-sm">
                      <p>✅ CORS configurado</p>
                      <p>✅ Rate limiting ativo</p>
                      <p>✅ Validação de entrada (Pydantic)</p>
                    </div>
                  </div>

                  <div className="bg-yellow-900 p-4 rounded-lg border border-yellow-500">
                    <p className="text-sm text-yellow-200">
                      ⚠️ Recomendação: Trocar JWT_SECRET em produção no arquivo <code className="bg-gray-900 px-2 py-1 rounded">backend/.env</code>
                    </p>
                  </div>
                </div>
              )}

              {configType === 'email' && editableEmail && (
                <div className="space-y-6">
                  <p className="text-gray-400">Configure o servidor SMTP e destinatários:</p>
                  
                  <div className="bg-gray-700 p-4 rounded-lg border-2 border-blue-600">
                    <h4 className="font-bold text-white mb-4">📧 Servidor SMTP</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-400">Host</label>
                        <input
                          type="text"
                          value={editableEmail.smtp?.host || 'smtp.gmail.com'}
                          onChange={(e) => setEditableEmail({
                            ...editableEmail,
                            smtp: {...editableEmail.smtp, host: e.target.value}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="smtp.gmail.com"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">Porta</label>
                        <input
                          type="number"
                          value={editableEmail.smtp?.port || 587}
                          onChange={(e) => setEditableEmail({
                            ...editableEmail,
                            smtp: {...editableEmail.smtp, port: parseInt(e.target.value)}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-sm text-gray-400">E-mail (Username)</label>
                        <input
                          type="email"
                          value={editableEmail.smtp?.username || ''}
                          onChange={(e) => setEditableEmail({
                            ...editableEmail,
                            smtp: {...editableEmail.smtp, username: e.target.value}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="seu@email.com"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-sm text-gray-400">Senha / App Password</label>
                        <input
                          type="password"
                          value={editableEmail.smtp?.password || ''}
                          onChange={(e) => setEditableEmail({
                            ...editableEmail,
                            smtp: {...editableEmail.smtp, password: e.target.value}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                          placeholder="••••••••"
                        />
                        <p className="text-xs text-gray-500 mt-1">Use App Password do Gmail se tiver 2FA ativado</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">De (E-mail)</label>
                        <input
                          type="email"
                          value={editableEmail.smtp?.from_email || ''}
                          onChange={(e) => setEditableEmail({
                            ...editableEmail,
                            smtp: {...editableEmail.smtp, from_email: e.target.value}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400">De (Nome)</label>
                        <input
                          type="text"
                          value={editableEmail.smtp?.from_name || 'Minha Composição'}
                          onChange={(e) => setEditableEmail({
                            ...editableEmail,
                            smtp: {...editableEmail.smtp, from_name: e.target.value}
                          })}
                          className="w-full bg-gray-800 text-white px-3 py-2 rounded mt-1"
                        />
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={editableEmail.smtp?.use_tls || false}
                            onChange={(e) => setEditableEmail({
                              ...editableEmail,
                              smtp: {...editableEmail.smtp, use_tls: e.target.checked}
                            })}
                            className="w-4 h-4"
                          />
                          <span className="text-gray-300">Usar TLS</span>
                        </label>
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={editableEmail.smtp?.use_ssl || false}
                            onChange={(e) => setEditableEmail({
                              ...editableEmail,
                              smtp: {...editableEmail.smtp, use_ssl: e.target.checked}
                            })}
                            className="w-4 h-4"
                          />
                          <span className="text-gray-300">Usar SSL</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-900/30 p-4 rounded-lg border border-green-500">
                    <p className="text-sm text-green-200">
                      ✅ As alterações serão salvas ao clicar em "Salvar Configurações"
                    </p>
                  </div>
                </div>
              )}

              {configType === 'seo' && (
                <div className="space-y-4">
                  <p className="text-gray-400">Otimizações SEO implementadas:</p>
                  
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-white mb-2">🌐 Meta Tags</h4>
                    <div className="space-y-2 text-sm">
                      <p>✅ Title e Description dinâmicos</p>
                      <p>✅ Open Graph (Facebook/Twitter)</p>
                      <p>✅ Canonical URLs</p>
                      <p>✅ Schema.org markup</p>
                    </div>
                  </div>

                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-white mb-2">🚀 Performance</h4>
                    <div className="space-y-2 text-sm">
                      <p>✅ React lazy loading</p>
                      <p>✅ Imagens otimizadas</p>
                      <p>✅ Código minificado</p>
                      <p>✅ Caching estratégico</p>
                    </div>
                  </div>

                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-white mb-2">📱 Mobile-First</h4>
                    <div className="space-y-2 text-sm">
                      <p>✅ Design responsivo</p>
                      <p>✅ Touch-friendly</p>
                      <p>✅ Fast loading</p>
                    </div>
                  </div>

                  <div className="bg-green-900 p-4 rounded-lg border border-green-500">
                    <p className="text-sm text-green-200">
                      ✅ Sistema otimizado para buscadores (Google, Bing, etc.)
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowConfigModal(false)}
                className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition"
                disabled={savingConfig}
              >
                Cancelar
              </button>
              {(configType === 'plans' || configType === 'email') && (
                <button
                  onClick={saveConfig}
                  disabled={savingConfig}
                  className={`flex-1 text-white py-3 rounded-lg transition ${
                    savingConfig 
                      ? 'bg-gray-600 cursor-not-allowed' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {savingConfig ? '⏳ Salvando...' : '✅ Salvar Configurações'}
                </button>
              )}
              {(configType === 'security' || configType === 'seo') && (
                <button
                  onClick={() => {
                    toast.info('ℹ️ Configurações informativas');
                    setShowConfigModal(false);
                  }}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition"
                >
                  OK, Entendi
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;